import React, { useContext, useEffect, useState } from 'react';
import { LanguageContext } from '../../../lib/i18n/LanguageProvider';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { ApiBaseResponse, ResponseCode } from '../../../models/Common/ApiBaseResponse';
import { GetHelpTopicDetailByTopicInfo } from '../../../services/AlbinaSoftComWebPortal';
import { GetHelpTopicDetailByTopicInfoRequest } from '../../../models/Request/GetHelpTopicDetailByTopicInfoRequest';
import { GetHelpTopicDetailByTopicInfoResponse, CompanyHelpTopicDetail, LineTypeEnum } from '../../../models/Response/GetHelpTopicDetailByTopicInfoResponse';
import { FaEnvelope, FaExclamationTriangle, FaLink, FaPhoneAlt, FaQuestionCircle } from 'react-icons/fa';
import CustomModal, { ElementType, SizeType, TabContent } from '../../../components/Modal';

type LayoutContextType = {
  setPageTitle: (title: string) => void;
};

const Topic: React.FC = () => {
  const context = useContext(LanguageContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const storedLang = localStorage.getItem('storedLang') || 'tr-TR';
  const [apiData, setApiData] = useState<GetHelpTopicDetailByTopicInfoResponse | null>(null);
  const { setPageTitle } = useOutletContext<LayoutContextType>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const id = searchParams.get('id') || "";
  const token = searchParams.get('t') || "";

  const missingParams: string[] = [];
  if (!id) missingParams.push('Company ID');
  if (!token) missingParams.push('Token');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (apiData) {
      const title = apiData ? `Yardım Portalı | ${apiData?.CompanyInfo.CompanyName}` : 'Yardım Portalı | Albinasoft A.Ş.';
      setPageTitle(title);
    }
  }, [apiData, setPageTitle]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const requestBody: GetHelpTopicDetailByTopicInfoRequest = {
        TopicId: parseInt(id || "0", 10),
        Token: token || ""
      };

      let response: ApiBaseResponse<GetHelpTopicDetailByTopicInfoResponse> | null = await GetHelpTopicDetailByTopicInfo(requestBody);

      if (response && response.Content && response.ResponseCode === ResponseCode.OK) {
        setApiData(response.Content);
      } else {
        console.warn("Yanıt beklenenden farklı:", response);
      }

    } catch (error: any) {
      console.error('API çağrısı başarısız:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!context) {
    return null;
  }

  if (missingParams.length > 0) {
    return (
      <div className="content-inner container-fluid pb-0">
        <div className="row">
          <div className="col-3"></div>
          <div className="col-md-6 col-12">
            <div className="card">
              <div className="card-header">
                <h3>Hatalı İşlem</h3>
              </div>
              <div className="card-body">
                <p>Eksik Parametreler:</p>
                <ul>
                  {missingParams.map(param => (
                    <li key={param}>{param}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-inner container-fluid pb-0">
        <div className="row">
          <div className="col-3"></div>
          <div className="col-md-6 col-12">
            <div className="card">
              <div className="card-header">
                <h3>Hatalı İşlem</h3>
              </div>
              <div className="card-body">
                <p>Hata:</p>
                {error}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loader simple-loader">
        <div className="loader-body"></div>
      </div>
    );
  }

  // Convert ApproveTextContent with <br/> tags for line breaks
  const modalContent: TabContent[] = [
    {
      tabName: "Onay Metni",
      elements: [
        {
          type: ElementType.TextArea,
          value: apiData?.TextApproveList[0]?.ApproveTextContent.replace(/\n/g, '<br/>') || "",
          className: "col-12",
          useHTML: true, // add a custom property to indicate this is HTML content
        }
      ]
    }
  ];

  const formattedWebUrl = apiData?.CompanyInfo?.WebUrl && !/^https?:\/\//i.test(apiData?.CompanyInfo?.WebUrl)
    ? `https://${apiData?.CompanyInfo?.WebUrl}`
    : apiData?.CompanyInfo?.WebUrl;

  return (
    <div>
      <Helmet>
        <title>
          {apiData ? `${apiData?.HelpTopicDetail?.HelpTopicHeader} | ${apiData?.CompanyInfo.CompanyName}` : 'Albinasoft A.Ş. | Yardım Portalı'}
        </title>
        <meta name="description" content={apiData ? `${apiData?.HelpTopicDetail.HelpTopicHeader}` : ''} />

        <meta property="og:title" content={apiData ? `${apiData?.CompanyInfo.CompanyName} Yardım Portalı` : 'Yardım Portalı'} />
        <meta property="og:description" content={apiData ? `${apiData?.HelpTopicDetail.HelpTopicHeader}` : ''} />
        <meta property="og:url" content={window.location.origin} />
      </Helmet>

      <div className="content-inner container-fluid pb-0">
        <div className="row">

          <div className="col-12">

            {apiData?.TextApproveList && apiData.TextApproveList.length > 0 ? (
              !isChecked ? (
                <div className="card">
                  <div className="card-body">
                    <div className="help-portal-widget">
                      <div>
                        <FaExclamationTriangle size={30} className={"text-danger"} />
                      </div>
                      <div className="help-portal-widget-detail">
                        <div>
                          <CustomModal
                            size={SizeType.lg}
                            show={isModalOpen}
                            onHide={() => setIsModalOpen(false)}
                            title="Onay Metni"
                            content={modalContent}
                            onCancel={() => setIsModalOpen(false)}
                          />
                          <div>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => setIsChecked(true)}
                            />
                            {" "}
                            <label>
                              <a onClick={() => setIsModalOpen(true)} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                                {apiData.TextApproveList[0]?.ApproveTextHeader}
                              </a>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <h2 className="mb-3 text-center">{apiData?.HelpTopicDetail?.HelpTopicHeader}</h2>
              )
            ) : (
              <h2 className="mb-3 text-center">{apiData?.HelpTopicDetail?.HelpTopicHeader}</h2>
            )}

          </div>

          {(isChecked || !apiData?.TextApproveList || apiData.TextApproveList.length === 0) && (
            <div className="col-12">
              {apiData?.CompanyHelpTopicDetails.map((detail: CompanyHelpTopicDetail, index: number) => (
                <div key={index}>
                  {detail.LineTypeInt === LineTypeEnum.Header && (
                    <div className="col-12 text-start align-items-center m-3">
                      <h4>{detail.Header}</h4>
                    </div>
                  )}
                  {detail.LineTypeInt === LineTypeEnum.Text && (
                    <div className="card">
                      <div className="card-body text-start align-items-center">
                        <div dangerouslySetInnerHTML={{ __html: detail.Text.replace(/\n/g, '<br/>') }} />
                      </div>
                    </div>
                  )}
                  {detail.LineTypeInt === LineTypeEnum.SimplePic && (
                    <div className="card">
                      <div className="card-body text-center align-items-center">
                        <img
                          src={detail.ImageUrl}
                          style={{
                            width: "75%",
                            height: "auto",
                            objectFit: "contain",
                          }}>
                        </img>
                      </div>
                    </div>
                  )}
                  {detail.LineTypeInt === LineTypeEnum.PicLeftTextRight && (
                    <div className="card">
                      <div className="card-body row align-items-center">
                        <div className="col-md-4 col-12 text-md-end text-center pb-3 pb-md-0">
                          <img
                            src={detail.ImageUrl}
                            style={{
                              width: "50%",
                              height: "auto",
                              objectFit: "contain",
                            }}>
                          </img>
                        </div>
                        <div className="col-md-8 col-12 text-md-start text-start">
                          <div dangerouslySetInnerHTML={{ __html: detail.Text.replace(/\n/g, '<br/>') }} />
                        </div>
                      </div>
                    </div>
                  )}
                  {detail.LineTypeInt === LineTypeEnum.PicRightTextLeft && (
                    <div className="card">
                      <div className="card-body row align-items-center">
                        <div className="col-md-8 col-12 order-2 order-md-1 text-md-end text-start">
                          <div dangerouslySetInnerHTML={{ __html: detail.Text.replace(/\n/g, '<br/>') }} />
                        </div>
                        <div className="col-md-4 col-12 order-1 order-md-2 text-md-start text-center pb-3 pb-md-0">
                          <img
                            src={detail.ImageUrl}
                            style={{
                              width: "50%",
                              height: "auto",
                              objectFit: "contain",
                            }}>
                          </img>

                        </div>
                      </div>
                    </div>
                  )}
                  {detail.LineTypeInt === LineTypeEnum.OneLineVideo && (
                    <div className="text-center align-items-center mb-5">
                      <div className="video-container">
                        <iframe
                          src={`https://www.youtube.com/embed/${detail.YoutTubeUrl}`}
                          title="YouTube video player"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="col-xl-4 col-12">
            <div className="card">
              <div className="card-body">
                <div className="help-portal-widget">
                  <div>
                    <FaPhoneAlt size={30} />
                  </div>
                  <div className="help-portal-widget-detail">
                    <p className="mb-2">Telefon</p>
                    <h4>
                      {apiData?.CompanyInfo.PhoneNo ? (
                        <a href={`tel:${apiData.CompanyInfo.PhoneNo}`}>
                          {apiData.CompanyInfo.PhoneNo}
                        </a>
                      ) : (
                        "Telefon numarası mevcut değil."
                      )}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-12">
            <div className="card">
              <div className="card-body">
                <div className="help-portal-widget">
                  <div>
                    <FaEnvelope size={30} />
                  </div>
                  <div className="help-portal-widget-detail">
                    <p className="mb-2">E-Posta</p>
                    <h4>
                      {apiData?.CompanyInfo.eMail ? (
                        <a href={`mailto:${apiData.CompanyInfo.eMail}`}>
                          {apiData.CompanyInfo.eMail}
                        </a>
                      ) : (
                        <p>E-posta bilgisi mevcut değil.</p>
                      )}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-12">
            <div className="card">
              <div className="card-body">
                <div className="help-portal-widget">
                  <div>
                    <FaLink size={30} />
                  </div>
                  <div className="help-portal-widget-detail">
                    <p className="mb-2">Web Sayfası</p>
                    <h4>
                      {formattedWebUrl ? (
                        <a href={formattedWebUrl} target="_blank" rel="noopener noreferrer">
                          {formattedWebUrl}
                        </a>
                      ) : (
                        <p>Web sitesi bilgisi mevcut değil.</p>
                      )}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default Topic;
